import { Appointment } from '../appointmentService';
import { RecurrencePattern, RecurrenceFrequency } from './RecurrencePattern';

// Define the AppointmentException interface directly in this file to avoid import issues
interface AppointmentException {
  id: string;
  appointmentId: string;
  exceptionDate: string;
  isDeleted: boolean;
  newStartTime?: string;
  newEndTime?: string;
  newTitle?: string;
  newDescription?: string;
  newAddress?: string;
  newIsAllDay?: boolean;
  createdAt: string;
  updatedAt: string;
}

// Extend the Appointment interface to include the properties we need
interface RecurringAppointment extends Appointment {
  isRecurringInstance?: boolean;
  recurringAppointmentId?: string;
  exceptionDate?: string;
}

export class RecurrenceGenerator {
  /**
   * Generate recurring instances of an appointment based on its recurrence rule
   * @param appointment The base appointment with recurrence rule
   * @param rangeStart The start date of the range to generate instances for
   * @param rangeEnd The end date of the range to generate instances for
   * @param exceptions Optional array of exceptions for this appointment
   * @returns Array of appointment instances
   */
  public static generateRecurringInstances(
    appointment: Appointment,
    rangeStart: Date,
    rangeEnd: Date,
    exceptions: AppointmentException[] = []
  ): RecurringAppointment[] {
    console.log(`Generating recurring instances for appointment: ${appointment.id}`, {
      title: appointment.title,
      recurrenceRule: appointment.recurrenceRule,
      rangeStart: rangeStart.toISOString(),
      rangeEnd: rangeEnd.toISOString(),
      rangeStartLocal: rangeStart.toLocaleDateString(),
      rangeEndLocal: rangeEnd.toLocaleDateString(),
      exceptionsCount: exceptions.length,
      timeZoneId: appointment.timeZoneId,
      originalStartTime: appointment.startTime,
      originalEndTime: appointment.endTime,
      originalStartDate: new Date(appointment.startTime).toISOString(),
      originalEndDate: new Date(appointment.endTime).toISOString(),
      originalStartLocal: new Date(appointment.startTime).toLocaleDateString(),
      originalEndLocal: new Date(appointment.endTime).toLocaleDateString(),
      originalDayOfWeek: new Date(appointment.startTime).getDay()
    });

    // Log all exceptions for debugging
    if (exceptions.length > 0) {
      console.log(`Exceptions for appointment ${appointment.id}:`, exceptions);
    }

    if (!appointment.recurrenceRule) {
      console.log(`Appointment ${appointment.id} has no recurrence rule, returning as is`);
      return [appointment as RecurringAppointment]; // Not a recurring appointment
    }

    // Parse the recurrence rule
    const pattern = RecurrencePattern.parseFromICalString(appointment.recurrenceRule);
    if (!pattern) {
      console.error(`Failed to parse recurrence rule: ${appointment.recurrenceRule}`);
      return [appointment as RecurringAppointment];
    }

    console.log(`Parsed recurrence pattern:`, {
      frequency: pattern.frequency,
      interval: pattern.interval,
      until: pattern.until,
      count: pattern.count,
      byDay: pattern.byDay,
      byMonthDay: pattern.byMonthDay,
      byMonth: pattern.byMonth,
      originalRule: appointment.recurrenceRule
    });

    // Calculate the duration of the appointment in milliseconds
    const startDate = new Date(appointment.startTime);
    const endDate = new Date(appointment.endTime);
    const durationMs = endDate.getTime() - startDate.getTime();

    // Create a map of exception dates for quick lookup
    const exceptionMap = new Map<string, AppointmentException>();
    for (const exception of exceptions) {
      // Ensure the exception date is in the correct format (YYYY-MM-DD)
      // This is critical for matching with the dateString later
      const normalizedExceptionDate = new Date(exception.exceptionDate).toISOString().split('T')[0];
      exceptionMap.set(normalizedExceptionDate, exception);
      console.log(`Added exception to map: ${normalizedExceptionDate}`, {
        id: exception.id,
        isDeleted: exception.isDeleted,
        newStartTime: exception.newStartTime,
        originalDate: exception.exceptionDate,
        normalizedDate: normalizedExceptionDate
      });
    }

    // Generate instances
    const instances: RecurringAppointment[] = [];
    
    // For weekly recurrence with multiple days, we need a different approach
    if (pattern.frequency === RecurrenceFrequency.WEEKLY && pattern.byDay && pattern.byDay.length > 0) {
      console.log(`Processing weekly recurrence with multiple days: ${pattern.byDay}`);
      
      // Generate instances for each week in the range
      let currentWeekStart = new Date(rangeStart);
      // Start from the beginning of the week containing the range start
      currentWeekStart.setDate(currentWeekStart.getDate() - currentWeekStart.getDay());
      
      const rangeEndWeek = new Date(rangeEnd);
      rangeEndWeek.setDate(rangeEndWeek.getDate() - rangeEndWeek.getDay() + 6); // End of week
      
      console.log(`Weekly generation range: ${currentWeekStart.toISOString()} to ${rangeEndWeek.toISOString()}`);
      
      while (currentWeekStart <= rangeEndWeek) {
        console.log(`Processing week starting: ${currentWeekStart.toISOString()}`);
        
        // Generate instances for each specified day in this week
        for (const dayOfWeek of pattern.byDay) {
          const instanceDate = new Date(currentWeekStart);
          instanceDate.setDate(instanceDate.getDate() + dayOfWeek);
          
          console.log(`Checking day ${dayOfWeek} (${instanceDate.toISOString()}) in range ${rangeStart.toISOString()} to ${rangeEnd.toISOString()}`);
          
          // Check if this instance falls within our requested range
          if (instanceDate >= rangeStart && instanceDate <= rangeEnd) {
            const dateString = instanceDate.toISOString().split('T')[0];
            console.log(`Generating instance for date: ${dateString} (day ${dayOfWeek})`);
            
            // Check for exceptions first
            const exception = exceptionMap.get(dateString);
            
            if (exception) {
              console.log(`Found exception for date ${dateString}:`, exception);
              
              // If the exception is marked as deleted, skip this instance
              if (exception.isDeleted) {
                console.log(`Instance for date ${dateString} is deleted, skipping`);
                continue;
              }
              
              // Create modified instance based on exception
              let instanceStartTime: string;
              let instanceEndTime: string;
              
              if (exception.newStartTime) {
                instanceStartTime = exception.newStartTime;
              } else {
                const originalStart = new Date(appointment.startTime);
                const instanceStartDate = new Date(instanceDate);
                instanceStartDate.setUTCHours(originalStart.getUTCHours(), originalStart.getUTCMinutes(), originalStart.getUTCSeconds(), originalStart.getUTCMilliseconds());
                instanceStartTime = instanceStartDate.toISOString();
              }
              
              if (exception.newEndTime) {
                instanceEndTime = exception.newEndTime;
              } else {
                const originalStart = new Date(appointment.startTime);
                const originalEnd = new Date(appointment.endTime);
                const durationMs = originalEnd.getTime() - originalStart.getTime();
                
                const instanceStartDate = new Date(instanceDate);
                instanceStartDate.setUTCHours(originalStart.getUTCHours(), originalStart.getUTCMinutes(), originalStart.getUTCSeconds(), originalStart.getUTCMilliseconds());
                
                const instanceEndDate = new Date(instanceStartDate.getTime() + durationMs);
                instanceEndTime = instanceEndDate.toISOString();
              }
              
              // Ensure the times have "Z" suffix to be treated as UTC
              if (instanceStartTime && !instanceStartTime.endsWith('Z')) {
                instanceStartTime = `${instanceStartTime}Z`;
              }
              
              if (instanceEndTime && !instanceEndTime.endsWith('Z')) {
                instanceEndTime = `${instanceEndTime}Z`;
              }
              
              const modifiedInstance: RecurringAppointment = {
                ...appointment,
                id: `${appointment.id}_${dateString}`,
                startTime: instanceStartTime,
                endTime: instanceEndTime,
                title: exception.newTitle || appointment.title,
                description: exception.newDescription || appointment.description,
                address: exception.newAddress || appointment.address,
                isAllDay: exception.newIsAllDay !== undefined ? exception.newIsAllDay : appointment.isAllDay,
                isRecurringInstance: true,
                recurringAppointmentId: appointment.id,
                recurrenceRule: appointment.recurrenceRule, // Preserve recurrence rule for offline functionality
                exceptionDate: dateString,
                timeZoneId: appointment.timeZoneId
              };
              
              console.log(`Added modified weekly instance for date ${dateString}:`, {
                id: modifiedInstance.id,
                title: modifiedInstance.title,
                startTime: modifiedInstance.startTime,
                endTime: modifiedInstance.endTime,
                dayOfWeek: dayOfWeek,
                timeZoneId: modifiedInstance.timeZoneId
              });
              
              instances.push(modifiedInstance);
            } else {
              // No exception, create regular instance
              const originalStart = new Date(appointment.startTime);
              const originalEnd = new Date(appointment.endTime);
              
              const instanceStartDate = new Date(instanceDate);
              instanceStartDate.setUTCHours(originalStart.getUTCHours(), originalStart.getUTCMinutes(), originalStart.getUTCSeconds(), originalStart.getUTCMilliseconds());
              
              const durationMs = originalEnd.getTime() - originalStart.getTime();
              const instanceEndDate = new Date(instanceStartDate.getTime() + durationMs);
              
              const instanceStartTime = instanceStartDate.toISOString();
              const instanceEndTime = instanceEndDate.toISOString();
              
              const instance: RecurringAppointment = {
                ...appointment,
                id: `${appointment.id}_${dateString}`,
                startTime: instanceStartTime,
                endTime: instanceEndTime,
                isRecurringInstance: true,
                recurringAppointmentId: appointment.id,
                recurrenceRule: appointment.recurrenceRule, // Preserve recurrence rule for offline functionality
                exceptionDate: undefined,
                timeZoneId: appointment.timeZoneId
              };
              
              console.log(`Added regular weekly instance for date ${dateString}:`, {
                id: instance.id,
                title: instance.title,
                startTime: instance.startTime,
                endTime: instance.endTime,
                dayOfWeek: dayOfWeek,
                timeZoneId: instance.timeZoneId,
                originalStartTime: appointment.startTime,
                originalEndTime: appointment.endTime,
                instanceDate: instanceDate.toISOString(),
                localDateString: new Date(instance.startTime).toLocaleDateString(),
                localDayOfWeek: new Date(instance.startTime).getDay()
              });
              
              instances.push(instance);
            }
          } else {
            console.log(`Day ${dayOfWeek} (${instanceDate.toISOString()}) is outside requested range, skipping`);
          }
        }
        
        // Move to next week
        currentWeekStart.setDate(currentWeekStart.getDate() + 7);
      }
    } else {
      // Use the original startDate directly to avoid timezone issues
      let currentDate = startDate;
      console.log(`Initial currentDate: ${currentDate.toISOString()}`);
      let count = 0;

      // If the pattern has an "until" date, make sure it's not beyond our range end
      const untilDate = pattern.until ? new Date(pattern.until) : null;
      const effectiveEndDate = untilDate && untilDate < rangeEnd ? untilDate : rangeEnd;

      console.log(`Generation parameters:`, {
        startDate: startDate.toISOString(),
        currentDate: currentDate.toISOString(),
        effectiveEndDate: effectiveEndDate.toISOString(),
        durationMs,
        timeZoneId: appointment.timeZoneId
      });

      // Generate instances until we reach the end date or count limit
      while (currentDate <= effectiveEndDate && (!pattern.count || count < pattern.count)) {
        // Include instances that OVERLAP the requested range, not only those starting within it
        // overlap if: start < rangeEnd AND end > rangeStart
        const instanceStart = currentDate;
        const instanceEnd = new Date(instanceStart.getTime() + durationMs);
        const overlapsRange = instanceStart < rangeEnd && instanceEnd > rangeStart;

        if (overlapsRange) {
        // Format the date as ISO string for exception lookup (just the date part)
        const dateString = currentDate.toISOString().split('T')[0];
        
        console.log(`Generating instance for date: ${dateString} (full date: ${currentDate.toISOString()})`);
        console.log(`Date analysis:`, {
          dateString,
          currentDateISO: currentDate.toISOString(),
          currentDateLocal: currentDate.toLocaleDateString(),
          currentDayOfWeek: currentDate.getDay(),
          originalDayOfWeek: new Date(appointment.startTime).getDay(),
          isSameDayOfWeek: currentDate.getDay() === new Date(appointment.startTime).getDay(),
          appointmentTimezone: appointment.timeZoneId
        });
        
        // Check if there's an exception for this date
        const exception = exceptionMap.get(dateString);
        
        if (exception) {
          console.log(`Found exception for date ${dateString}:`, exception);
          
          // If the exception is marked as deleted, skip this instance
          if (exception.isDeleted) {
            console.log(`Instance for date ${dateString} is deleted, skipping`);
            currentDate = this.getNextOccurrence(currentDate, pattern);
            count++;
            continue;
          }
          
          // Otherwise, create a modified instance based on the exception
          let instanceStartTime: string;
          let instanceEndTime: string;
          
          if (exception.newStartTime) {
            // Use the exception's new start time
            instanceStartTime = exception.newStartTime;
          } else {
            // Preserve the original time but use the current date
            const originalStart = new Date(appointment.startTime);
            const instanceStartDate = new Date(currentDate);
            instanceStartDate.setUTCHours(originalStart.getUTCHours(), originalStart.getUTCMinutes(), originalStart.getUTCSeconds(), originalStart.getUTCMilliseconds());
            instanceStartTime = instanceStartDate.toISOString();
          }
          
          if (exception.newEndTime) {
            // Use the exception's new end time
            instanceEndTime = exception.newEndTime;
          } else {
            // Calculate duration from original appointment and apply to current date
            const originalStart = new Date(appointment.startTime);
            const originalEnd = new Date(appointment.endTime);
            const durationMs = originalEnd.getTime() - originalStart.getTime();
            
            const instanceStartDate = new Date(currentDate);
            instanceStartDate.setUTCHours(originalStart.getUTCHours(), originalStart.getUTCMinutes(), originalStart.getUTCSeconds(), originalStart.getUTCMilliseconds());
            
            const instanceEndDate = new Date(instanceStartDate.getTime() + durationMs);
            instanceEndTime = instanceEndDate.toISOString();
          }

          // Ensure the times have "Z" suffix to be treated as UTC
          if (instanceStartTime && !instanceStartTime.endsWith('Z')) {
            instanceStartTime = `${instanceStartTime}Z`;
          }

          if (instanceEndTime && !instanceEndTime.endsWith('Z')) {
            instanceEndTime = `${instanceEndTime}Z`;
          }

          console.log(`Creating modified instance with start time: ${instanceStartTime} (from exception.newStartTime: ${exception.newStartTime})`);
          console.log(`Ensuring times are in UTC format:`, {
            originalStart: exception.newStartTime,
            correctedStart: instanceStartTime,
            originalEnd: exception.newEndTime,
            correctedEnd: instanceEndTime,
            timeZoneId: appointment.timeZoneId
          });
          
          const modifiedInstance: RecurringAppointment = {
            ...appointment,
            id: `${appointment.id}_${dateString}`, // Generate a unique ID for this instance
            startTime: instanceStartTime,
            endTime: instanceEndTime,
            title: exception.newTitle || appointment.title,
            description: exception.newDescription || appointment.description,
            address: exception.newAddress || appointment.address,
            isAllDay: exception.newIsAllDay !== undefined ? exception.newIsAllDay : appointment.isAllDay,
            isRecurringInstance: true,
            recurringAppointmentId: appointment.id,
            recurrenceRule: appointment.recurrenceRule, // Preserve recurrence rule for offline functionality
            exceptionDate: dateString,
            timeZoneId: appointment.timeZoneId // Ensure timezone is preserved
          };
          
          console.log(`Added modified instance for date ${dateString}:`, {
            id: modifiedInstance.id,
            title: modifiedInstance.title,
            startTime: modifiedInstance.startTime,
            originalDate: dateString,
            exceptionDate: modifiedInstance.exceptionDate,
            timeZoneId: modifiedInstance.timeZoneId
          });
          
          instances.push(modifiedInstance);
        } else {
          // No exception, create a regular instance
          // CRITICAL: Preserve the exact time from the original appointment by using the original start/end times
          // but adjusting only the date part to match the current occurrence
          const originalStart = new Date(appointment.startTime);
          const originalEnd = new Date(appointment.endTime);
          
          // Calculate the duration from the original appointment
          const durationMs = originalEnd.getTime() - originalStart.getTime();
          
          // TIMEZONE FIX: Preserve the local time in the appointment's timezone
          // The key insight: we need to maintain the same local time on the same day of the week
          const instanceStartDate = new Date(currentDate);
          
          // Convert the original UTC time to the appointment's timezone to get the local time components
          // This ensures that if the original was 11 PM Wednesday in Auckland time, 
          // the instance will also be 11 PM Wednesday in Auckland time
          
          // Extract the original start's wall-clock time in the appointment's
          // timezone via Intl parts. This avoids round-tripping a localized date
          // string through the Date constructor, which has no defined parsing for
          // non-ISO formats (e.g. "DD/MM/YYYY, HH:mm:ss") and can yield a wrong or
          // Invalid Date depending on the day/month values.
          const timeZoneId = appointment.timeZoneId || undefined;
          const timeParts = new Intl.DateTimeFormat("en-GB", {
            timeZone: timeZoneId,
            hourCycle: "h23",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit"
          }).formatToParts(originalStart);
          const getTimePart = (type: string): number =>
            parseInt(timeParts.find(p => p.type === type)?.value ?? "0", 10);

          // Get the local time components. Seconds and milliseconds are
          // timezone-invariant (UTC offsets are always whole minutes).
          const localTimeComponents = {
            hours: getTimePart("hour"),
            minutes: getTimePart("minute"),
            seconds: getTimePart("second"),
            milliseconds: originalStart.getUTCMilliseconds()
          };
          
          // Apply this local time to the new date
          instanceStartDate.setHours(localTimeComponents.hours, localTimeComponents.minutes, localTimeComponents.seconds, localTimeComponents.milliseconds);
          
          console.log(`Time application analysis:`, {
            originalStartUTC: originalStart.toISOString(),
            originalStartLocal: originalStart.toLocaleString(),
            appointmentTimeZone: timeZoneId ?? "(browser default)",
            localTimeComponents,
            currentDateBefore: currentDate.toISOString(),
            instanceStartDateAfter: instanceStartDate.toISOString(),
            instanceStartDateLocal: instanceStartDate.toLocaleString(),
            finalDayOfWeek: instanceStartDate.getDay(),
            originalDayOfWeek: new Date(appointment.startTime).getDay()
          });
          
          // Create end date by adding the duration to the start date
          const instanceEndDate = new Date(instanceStartDate.getTime() + durationMs);
          
          const instanceStartTime = instanceStartDate.toISOString();
          const instanceEndTime = instanceEndDate.toISOString();
          
          console.log(`Creating regular instance with preserved time:`, {
            originalStart: appointment.startTime,
            originalEnd: appointment.endTime,
            currentDate: currentDate.toISOString(),
            instanceStartTime,
            instanceEndTime,
            timeZoneId: appointment.timeZoneId
          });
          
          const instance: RecurringAppointment = {
            ...appointment,
            id: `${appointment.id}_${dateString}`, // Generate a unique ID for this instance
            startTime: instanceStartTime,
            endTime: instanceEndTime,
            isRecurringInstance: true,
            recurringAppointmentId: appointment.id,
            recurrenceRule: appointment.recurrenceRule, // Preserve recurrence rule for offline functionality
            exceptionDate: undefined,
            timeZoneId: appointment.timeZoneId // Ensure timezone is preserved
          };
          
          console.log(`Added regular instance for date ${dateString}:`, {
            id: instance.id,
            title: instance.title,
            startTime: instance.startTime,
            endTime: instance.endTime,
            originalDate: dateString,
            timeZoneId: instance.timeZoneId,
            dayOfWeek: new Date(instance.startTime).getDay(),
            originalStartTime: appointment.startTime,
            originalEndTime: appointment.endTime,
            currentDate: currentDate.toISOString(),
            localDateString: new Date(instance.startTime).toLocaleDateString(),
            localDayOfWeek: new Date(instance.startTime).getDay()
          });
          
          instances.push(instance);
        }
      } else {
        console.log(`Instance date ${currentDate.toISOString()} is outside requested range, skipping`);
      }
      
        // Move to the next occurrence
        const nextDate = this.getNextOccurrence(currentDate, pattern);
        console.log(`Moving to next occurrence: ${currentDate.toISOString()} -> ${nextDate.toISOString()}`);
        
        // Prevent infinite loops if getNextOccurrence doesn't advance the date
        if (nextDate <= currentDate) {
          console.error(`Next date (${nextDate.toISOString()}) is not after current date (${currentDate.toISOString()}), breaking loop to prevent infinite recursion`);
          break;
        }
        
        currentDate = nextDate;
        count++;
        
        // Safety limit to prevent infinite loops
        if (count > 1000) {
          console.error(`Reached maximum iteration count (1000), breaking loop to prevent infinite recursion`);
          break;
        }
      }
    }
    
    console.log(`Generated ${instances.length} instances for appointment ${appointment.id}`);
    return instances;
  }

  /**
   * Get the next occurrence based on the recurrence pattern
   * @param currentDate The current date
   * @param pattern The recurrence pattern
   * @returns The next occurrence date
   */
  private static getNextOccurrence(currentDate: Date, pattern: RecurrencePattern): Date {
    // Create a new date object to avoid modifying the original
    const nextDate = new Date(currentDate.getTime());
    
    switch (pattern.frequency) {
      case RecurrenceFrequency.DAILY:
        nextDate.setDate(nextDate.getDate() + pattern.interval);
        break;
        
      case RecurrenceFrequency.WEEKLY:
        console.log(`Processing WEEKLY recurrence:`, {
          byDay: pattern.byDay,
          interval: pattern.interval,
          currentDayOfWeek: nextDate.getDay(),
          currentDate: nextDate.toISOString()
        });
        
        if (pattern.byDay && pattern.byDay.length > 0) {
          // Find the next day of the week that matches one in byDay
          const currentDayOfWeek = nextDate.getDay();
          let daysToAdd = 1; // Start by adding one day
          
          console.log(`Looking for next day in byDay: ${pattern.byDay}, current day: ${currentDayOfWeek}`);
          
          // Try each day until we find one that matches
          for (let i = 0; i < 7; i++) {
            const nextDayOfWeek = (currentDayOfWeek + daysToAdd) % 7;
            
            console.log(`Checking day ${nextDayOfWeek} (daysToAdd: ${daysToAdd})`);
            
            // Check if this day is in byDay
            if (pattern.byDay.includes(nextDayOfWeek)) {
              console.log(`Found matching day: ${nextDayOfWeek}`);
              break;
            }
            
            daysToAdd++;
          }
          
          console.log(`Adding ${daysToAdd} days to get to next occurrence`);
          nextDate.setDate(nextDate.getDate() + daysToAdd);
        } else {
          // Simple weekly recurrence
          console.log(`Simple weekly recurrence: adding ${7 * pattern.interval} days`);
          nextDate.setDate(nextDate.getDate() + (7 * pattern.interval));
        }
        break;
        
      case RecurrenceFrequency.MONTHLY:
        if (pattern.byMonthDay && pattern.byMonthDay.length > 0) {
          // Use the first day specified in byMonthDay
          const targetDay = pattern.byMonthDay[0];
          
          // Move to the next month
          nextDate.setMonth(nextDate.getMonth() + pattern.interval);
          
          // Set the day of the month
          const daysInMonth = new Date(nextDate.getFullYear(), nextDate.getMonth() + 1, 0).getDate();
          nextDate.setDate(Math.min(targetDay, daysInMonth));
        } else if (pattern.byDay && pattern.byDay.length > 0) {
          // Handle "nth day of month" (e.g., "2nd Monday")
          // This is a simplified implementation
          const dayOfWeek = pattern.byDay[0] % 7; // Day of week (0-6)
          const weekOfMonth = Math.floor(pattern.byDay[0] / 7); // Week of month (0-4)
          
          // Move to the next month
          nextDate.setMonth(nextDate.getMonth() + pattern.interval);
          nextDate.setDate(1); // Start from the first day of the month
          
          // Find the first occurrence of the day of week
          while (nextDate.getDay() !== dayOfWeek) {
            nextDate.setDate(nextDate.getDate() + 1);
          }
          
          // Add weeks as needed
          nextDate.setDate(nextDate.getDate() + (weekOfMonth * 7));
          
          // If we've gone into the next month, go back to the last occurrence in the current month
          if (nextDate.getMonth() > (currentDate.getMonth() + pattern.interval) % 12) {
            nextDate.setDate(nextDate.getDate() - 7);
          }
        } else {
          // Simple monthly recurrence (same day each month)
          nextDate.setMonth(nextDate.getMonth() + pattern.interval);
        }
        break;
        
      case RecurrenceFrequency.YEARLY:
        if (pattern.byMonth && pattern.byMonth.length > 0 && pattern.byMonthDay && pattern.byMonthDay.length > 0) {
          // Specific day of specific month
          const targetMonth = pattern.byMonth[0] - 1; // Convert from 1-based to 0-based
          const targetDay = pattern.byMonthDay[0];
          
          // Move to the next year
          nextDate.setFullYear(nextDate.getFullYear() + pattern.interval);
          
          // Set the month and day
          nextDate.setMonth(targetMonth);
          
          // Ensure the day is valid for the month
          const daysInMonth = new Date(nextDate.getFullYear(), targetMonth + 1, 0).getDate();
          nextDate.setDate(Math.min(targetDay, daysInMonth));
        } else {
          // Simple yearly recurrence (same day and month each year)
          nextDate.setFullYear(nextDate.getFullYear() + pattern.interval);
        }
        break;
    }
    
    return nextDate;
  }

  /**
   * Get the next occurrence of a specific day of the week
   * @param date The starting date
   * @param dayOfWeek The day of the week (0-6, where 0 is Sunday)
   * @returns The next date that falls on the specified day of the week
   */
  private static getNextWeekday(date: Date, dayOfWeek: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + (7 + dayOfWeek - date.getDay()) % 7);
    return result;
  }

  /**
   * Check if two dates fall on the same day
   * @param date1 The first date
   * @param date2 The second date
   * @returns True if the dates fall on the same day, false otherwise
   */
  public static isSameDay(date1: Date, date2: Date): boolean {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }
} 